import { Filter } from '@cubejs-client/core';

import {
  CUBE_INSIGHT_EMPLOYEE_FIELD_FORCE,
  CUBE_INSIGHT_PRODUCT,
  CUBE_INSIGHT_TAG,
  INSIGHT_CUBE_NAME,
} from '@/api/hcp/interaction/types';
import { CountWithComparisonAndChange } from '@/models/common';
import { therapeuticIndicationDimensions } from '@/models/filter/utils/hierarchicalFilter';
import { parseTaIndicationFilter } from '@/models/filter/utils/parseTaIndicationFilter';

import { InsightFilters, InsightsFilterName } from '@/pages/Insights/types/store';

export const INSIGHTS_NOTE_DISMISSED_STORAGE_KEY = 'insights_note_dismissed';

export const EMPTY_COUNT: CountWithComparisonAndChange = { current: 0, previous: 0, change: 'N/A' };

export const INSIGHT_PRODUCT_INDICATION_DIMENSIONS = therapeuticIndicationDimensions(CUBE_INSIGHT_PRODUCT);

export const INSIGHT_DATE_DIMENSION = `${INSIGHT_CUBE_NAME}.insight_created_date`;

export const INSIGHT_TAG_DIMENSIONS = [
  `${CUBE_INSIGHT_TAG}.local_tag_level_1`,
  `${CUBE_INSIGHT_TAG}.local_tag_level_2`,
  `${CUBE_INSIGHT_TAG}.local_tag_level_3`,
  `${CUBE_INSIGHT_TAG}.local_tag_level_4`,
];

export const FILTER_DIMENSION_MAP = {
  product: `${CUBE_INSIGHT_PRODUCT}.product`,
  fieldForceCode: `${CUBE_INSIGHT_EMPLOYEE_FIELD_FORCE}.employee_insight_field_force_code`,
  employeeType: `${INSIGHT_CUBE_NAME}.employee_type`,
  employeeName: `${INSIGHT_CUBE_NAME}.created_by_employee`,
} satisfies Partial<Record<InsightsFilterName, string>>;

export type ActiveInsightsFilterName = keyof typeof FILTER_DIMENSION_MAP;

export function isActiveFilterName(key: string): key is ActiveInsightsFilterName {
  return key in FILTER_DIMENSION_MAP;
}

let cachedInsightIndicationPaths: string[] = [];
let cachedInsightTagPaths: string[] = [];

type Listener = () => void;

const indicationPathListeners = new Set<Listener>();
const tagPathListeners = new Set<Listener>();

export const subscribeToIndicationPaths = (listener: Listener): (() => void) => {
  indicationPathListeners.add(listener);
  return () => indicationPathListeners.delete(listener);
};

export const getIndicationPathsSnapshot = (): string[] => cachedInsightIndicationPaths;

export const subscribeToTagPaths = (listener: Listener): (() => void) => {
  tagPathListeners.add(listener);
  return () => tagPathListeners.delete(listener);
};

export const getTagPathsSnapshot = (): string[] => cachedInsightTagPaths;

interface HierarchicalPaths {
  indicationPaths: string[];
  tagPaths: string[];
}

export function buildCrossFilters(
  filters: InsightFilters,
  excludeFilter: InsightsFilterName,
  paths: HierarchicalPaths,
): Filter[] {
  return Object.entries(filters).reduce<Filter[]>((crossFilterList, [filterName, selectedValues]) => {
    if (filterName === excludeFilter || !Array.isArray(selectedValues) || !selectedValues.length) {
      return crossFilterList;
    }

    if (filterName === 'indication') {
      const indicationFilter = parseTaIndicationFilter(
        filters.indication,
        paths.indicationPaths,
        INSIGHT_PRODUCT_INDICATION_DIMENSIONS,
      );
      if (indicationFilter) crossFilterList.push(indicationFilter);
      return crossFilterList;
    }

    if (filterName === 'tag') {
      const tagFilter = parseTaIndicationFilter(filters.tag, paths.tagPaths, INSIGHT_TAG_DIMENSIONS);
      if (tagFilter) crossFilterList.push(tagFilter);
      return crossFilterList;
    }

    if (!isActiveFilterName(filterName)) {
      return crossFilterList;
    }

    crossFilterList.push({
      member: FILTER_DIMENSION_MAP[filterName],
      operator: 'equals',
      values: [...selectedValues],
    });
    return crossFilterList;
  }, []);
}

export const setCachedInsightIndicationPaths = (paths: string[]): void => {
  cachedInsightIndicationPaths = paths;
  indicationPathListeners.forEach(listener => listener());
};

export const getCachedInsightIndicationPaths = (): string[] => cachedInsightIndicationPaths;

export const setCachedInsightTagPaths = (paths: string[]): void => {
  cachedInsightTagPaths = paths;
  tagPathListeners.forEach(listener => listener());
};

export const getCachedInsightTagPaths = (): string[] => cachedInsightTagPaths;
